import './styles.css';
import { SlotGame } from './game/SlotGame.ts';
import { ProviderClient } from './provider/ProviderClient.ts';
import { sfx } from './audio/sfx.ts';
import { rollup, type RollupHandle } from './utils/rollup.ts';

// Build marker — check the iframe's devtools console to confirm the live build.
console.log('[slotbattles] client build: paylines-v1');

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing #${id}`);
  return el as T;
};

const els = {
  stage: $('stage'),
  spinsNum: $('spins-num'),
  totalWin: $('total-win'),
  balance: $('balance'),
  spinBtn: $('spin'),
  overlay: $('overlay'),
  overlayText: $('overlay-text'),
  featureTitle: $('feature-title'),
};

const token = new URLSearchParams(location.search).get('session');
const embedded = window.parent !== window.self;
const game = new SlotGame();
let client: ProviderClient | null = null;

const state = { spinsLeft: 0, totalWin: 0, balance: 0, bet: 0, busy: false, status: 'playing' as string, special: '' };

const delay = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

function postParent(type: string, payload?: unknown): void {
  if (embedded) window.parent.postMessage({ source: 'slotbattles-slot', type, payload }, '*');
}

function fmt(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

function niceName(id: string): string {
  return (id || 'special').toUpperCase();
}

// One active count-up per HUD meter; a new spin cancels the previous one.
const hudRollups: Record<'totalWin' | 'balance', RollupHandle | null> = { totalWin: null, balance: null };
const hudShown = { totalWin: 0, balance: 0 };

function tickMeter(key: 'totalWin' | 'balance', el: HTMLElement, to: number): void {
  hudRollups[key]?.cancel();
  const from = hudShown[key];
  if (from === to) {
    el.textContent = fmt(to);
    return;
  }
  hudRollups[key] = rollup(from, to, 600, (v) => {
    hudShown[key] = v;
    el.textContent = fmt(Number.isInteger(to) ? Math.round(v) : Math.round(v * 100) / 100);
  });
}

function renderHud(): void {
  els.spinsNum.textContent = String(state.spinsLeft);
  tickMeter('totalWin', els.totalWin, state.totalWin);
  tickMeter('balance', els.balance, state.balance);
}

function showLoading(text = 'Loading…'): void {
  els.overlayText.textContent = text;
  els.overlay.classList.add('loading');
  els.overlay.classList.remove('hidden');
}
function showOverlay(text: string): void {
  els.overlayText.textContent = text;
  els.overlay.classList.remove('loading');
  els.overlay.classList.remove('hidden');
}
function hideOverlay(): void {
  els.overlay.classList.add('hidden');
}

interface SpinOutcome { done: boolean; won: boolean; win: number; }

async function runOneSpin(): Promise<SpinOutcome> {
  if (!client) return { done: true, won: false, win: 0 };
  state.busy = true;
  els.spinBtn.classList.add('spinning');

  const r = await client.spin();
  sfx.reelStop();

  const won = await game.spin(r);

  state.spinsLeft = r.spinsLeft;
  state.totalWin = r.totalWin;
  state.balance = r.balance;
  state.busy = false;
  els.spinBtn.classList.remove('spinning');
  if (r.done) state.status = 'completed';
  renderHud();

  postParent('SCORE', { totalWin: state.totalWin, spinsLeft: state.spinsLeft });
  if (r.done) {
    postParent('FEATURE_COMPLETE', { totalWinAmount: state.totalWin });
    sfx.featureComplete();
    showOverlay(`FEATURE COMPLETE\n+${fmt(state.totalWin)}`);
  }
  return { done: r.done, won, win: r.win };
}

// Auto-run the free-spins feature: a short intro, then spin after spin until done.
async function autoplay(): Promise<void> {
  sfx.resume();
  sfx.featureStart();
  showOverlay(`${state.spinsLeft} FREE SPINS\n${niceName(state.special)} EXPANDS`);
  await delay(1600);
  hideOverlay();

  while (state.spinsLeft > 0 && state.status === 'playing') {
    let outcome: SpinOutcome;
    try {
      outcome = await runOneSpin();
    } catch (err) {
      console.error('[slot] spin failed, retrying:', err);
      state.busy = false;
      els.spinBtn.classList.remove('spinning');
      await delay(1200);
      continue;
    }
    if (outcome.done) break;
    // Brief pause between spins; the win presenter already provides the linger.
    await delay(outcome.won ? 300 : 420);
  }
}

(async () => {
  showLoading('Loading…');

  try {
    await game.init(els.stage);
  } catch (err) {
    console.error('[slot] init failed:', err);
    showOverlay('Failed to load game');
    return;
  }

  if (!token) {
    showOverlay('No session');
    return;
  }

  client = new ProviderClient(token);
  try {
    const cfg = await client.config();
    game.setPaylines(cfg.paylines);
    game.setBet(cfg.bet);
    state.spinsLeft = cfg.spinsLeft;
    state.totalWin = cfg.totalWin;
    state.balance = cfg.balance;
    hudShown.totalWin = cfg.totalWin;
    hudShown.balance = cfg.balance;
    state.bet = cfg.bet;
    state.status = cfg.status;
    state.special = cfg.special;
    els.featureTitle.textContent = `${cfg.featureSpins} FREE SPINS`;
    renderHud();
    postParent('READY');

    if (cfg.status === 'completed') {
      showOverlay(`FEATURE COMPLETE\n+${fmt(cfg.totalWin)}`);
    } else {
      void autoplay();
    }
  } catch (err) {
    console.error('[slot] config failed:', err);
    showOverlay('Session expired');
  }
})();
